import { createSelector } from "reselect"
import { ATTRIBUTION_COLORS, PERFORMANCE_OVERALL_ITEMS } from "./analyzeConstants"
import { getIsRfrAttributionEnabled } from "selectors/features"

export const RESIDUAL_RETURN = "residualReturn"

/**
 * Residual item definition for performance charts and selectors.
 * Used in analyze-performance and analyze-composite-performance modules.
 */
export const RESIDUAL_ITEM = {
  id: "residual",
  color: ATTRIBUTION_COLORS.RISK_FREE,
  label: "Residual",
  field: "residual",
  type: "overall",
} as const

/**
 * Generates residual attribution summary subquery for `attribution.summary`.
 * @param includeCurrency - Whether to include cumulativeCurrency field
 */
export function getResidualAttributionSummarySubquery(includeCurrency = false): string {
  return includeCurrency
    ? "residual { cumulativePercent, cumulativeCurrency }"
    : "residual { cumulativePercent }"
}

/**
 * Generates residual return subquery for performance contributors.
 * @param includeCurrency - Whether to include cumulativeCurrency field
 */
export function getResidualReturnSubquery(includeCurrency = false): string {
  return includeCurrency
    ? "residualReturn { cumulativePercent, cumulativeCurrency }"
    : "residualReturn { cumulativePercent }"
}

/**
 * Generates residual period performance subquery for annualized metrics.
 */
export function getResidualPeriodPerformanceSubquery(): string {
  return "residual { annualizedReturns, annualizedVolatility }"
}

/**
 * Selector that returns PERFORMANCE_OVERALL_ITEMS with RESIDUAL_ITEM appended
 * when RFR attribution (feature flag) is enabled.
 * Used in analyze-performance, analyze-composite-performance, and security-profile-performance modules.
 */
export const getPerformanceOverallItemsWithResidual = createSelector(
  [getIsRfrAttributionEnabled],
  (isRfrAttributionEnabled) =>
    isRfrAttributionEnabled ? [...PERFORMANCE_OVERALL_ITEMS, RESIDUAL_ITEM] : PERFORMANCE_OVERALL_ITEMS,
)
